import { ISaveManager, SavedData, SaveManagerId } from './ISaveManager';
import { logger } from '../Analytics/Logger';
import UniversalCookie from 'universal-cookie';

interface SaveDataPayload<T = any> {
  saveDataVersion: number;
  data: T;
}

const CURRENT_SAVE_DATA_VERSION = 2;
const FOREVER_DATE = new Date(3000, 1, 1);

/**
 * LocalSaveManager - saves game data to localStorage/cookies
 * Always enabled, provides instant local persistence
 */
export class LocalSaveManager<TGameData = any> implements ISaveManager<TGameData> {
  readonly id = SaveManagerId.Local;
  private readonly cookies = new UniversalCookie({}, { expires: FOREVER_DATE });

  constructor() {}

  /**
   * Local save is always enabled
   */
  isEnabled(): boolean {
    return true;
  }

  /**
   * Generate cookie name for a specific game and data type
   */
  private getCookieName(gameId: string, dataName: string): string {
    return `${gameId}_${dataName}`;
  }

  /**
   * Save game data to localStorage/cookies
   */
  async save(gameId: string, playerId: string, data: TGameData, timestamp: number, deviceId: string, expirationDate?: Date): Promise<void> {
    try {
      const cookieName = this.getCookieName(gameId, 'gameState');
      const savePayload: SaveDataPayload = {
        saveDataVersion: CURRENT_SAVE_DATA_VERSION,
        data: {
          data,
          timestamp,
          playerId,
          deviceId, // Store deviceId with local save
        },
      };
      
      this.cookies.set(cookieName, savePayload, { 
        path: '/', 
        expires: expirationDate || FOREVER_DATE
      });
      
      logger.debug(`[LocalSaveManager] Saved ${gameId} for player ${playerId} (device: ${deviceId})`);
    } catch (error) {
      logger.error('[LocalSaveManager] Failed to save:', error);
      throw error;
    }
  }

  /**
   * Load game data from localStorage/cookies
   */
  async load(gameId: string, playerId: string): Promise<SavedData<TGameData> | null> {
    try {
      const cookieName = this.getCookieName(gameId, 'gameState');
      const savedPayload = this.cookies.get(cookieName) as SaveDataPayload | undefined;
      
      if (!savedPayload?.data) {
        return null;
      }

      return {
        savedGameData: savedPayload.data.data,
        timestamp: savedPayload.data.timestamp || 0,
        source: this.id,
        deviceId: savedPayload.data.deviceId, // Include deviceId from local save
      };
    } catch (error) {
      logger.error('[LocalSaveManager] Failed to load:', error);
      return null;
    }
  }

  /**
   * Local save has lower priority than cloud (0 < 10)
   */
  getPriority(): number {
    return 0;
  }

  /**
   * Static utility methods for simple cookie operations
   * Used by auth and analytics systems that don't need the full save manager infrastructure
   */

  /**
   * Generate cookie name for simple data storage
   */
  static getCookieName(namespace: string, dataName: string): string {
    return `${namespace}_${dataName}`;
  }

  /**
   * Load simple data from cookies
   * @param namespace Namespace for the data (e.g., game ID, 'auth', 'analytics')
   * @param dataName Name of the data field
   * @returns SaveDataPayload with typed data field, or undefined if not found
   */
  static loadSimpleData<T = any>(namespace: string, dataName: string): SaveDataPayload<T> | undefined {
    const cookies = new UniversalCookie({}, { expires: FOREVER_DATE });
    const cookieName = LocalSaveManager.getCookieName(namespace, dataName);
    const cookieValue = cookies.get(cookieName);
    if (!cookieValue) {
      return undefined;
    }
    try {
      return cookieValue as SaveDataPayload<T>;
    } catch (error) {
      logger.error(`[LocalSaveManager] Failed to parse cookie ${cookieName}:`, error);
      return undefined;
    }
  }

  /**
   * Save simple data to cookies
   * @param namespace Namespace for the data (e.g., game ID, 'auth', 'analytics')
   * @param dataName Name of the data field
   * @param data Data to save (will be wrapped in SaveDataPayload if needed)
   * @param expirationDate Optional expiration date (defaults to 7 days)
   */
  static saveSimpleData(
    namespace: string,
    dataName: string,
    data: SaveDataPayload | any,
    expirationDate: Date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  ): void {
    const cookies = new UniversalCookie({}, { expires: FOREVER_DATE });
    const cookieName = LocalSaveManager.getCookieName(namespace, dataName);
    
    // If data already has saveDataVersion, use it as-is; otherwise wrap it
    const saveDataToStore: SaveDataPayload = 
      (typeof data === 'object' && data !== null && 'saveDataVersion' in data && typeof data.saveDataVersion === 'number')
        ? data
        : { saveDataVersion: CURRENT_SAVE_DATA_VERSION, data };
    
    cookies.set(cookieName, saveDataToStore, { path: '/', expires: expirationDate });
  }

  /**
   * Clear/delete simple data from cookies
   * @param namespace Namespace for the data (e.g., game ID, 'auth', 'analytics')
   * @param dataName Name of the data field
   */
  static clearSimpleData(namespace: string, dataName: string): void {
    const cookies = new UniversalCookie({}, { expires: FOREVER_DATE });
    const cookieName = LocalSaveManager.getCookieName(namespace, dataName);
    cookies.remove(cookieName, { path: '/' });
  }
}
